import { useEffect, useMemo } from "react";

import { PRESETS } from "../data/engine";
import type { useKagami } from "../state/useKagami";
import { Chip, mutedColor } from "./primitives";

type K = ReturnType<typeof useKagami>;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Most frequent values first — the taste summary is just a tally. */
function tally(values: string[]): string[] {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
}

export function ProfileDrawer({ k }: { k: K }) {
  useEffect(() => {
    if (!k.profileOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") k.setProfileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [k]);

  const taste = useMemo(() => {
    const g = tally(k.libraryWorks.flatMap((c) => c.genres)).slice(0, 3);
    const m = tally(k.libraryWorks.flatMap((c) => c.moods)).slice(0, 3).map(cap);
    return [...g, ...m];
  }, [k.libraryWorks]);

  if (!k.profileOpen) return null;

  const completed = k.libraryWorks.filter((c) => k.library[c.id] === "Completed").length;
  const avg = k.libraryWorks.length
    ? (Math.round((k.libraryWorks.reduce((a, c) => a + c.score, 0) / k.libraryWorks.length) * 10) / 10).toFixed(1)
    : "—";

  const stats = [
    { val: String(k.libraryWorks.length), label: "In library" },
    { val: String(completed), label: "Completed" },
    { val: avg, label: "Avg score" },
  ];

  return (
    <div>
      <div onClick={() => k.setProfileOpen(false)} style={{
        position: "fixed", inset: 0, zIndex: 70,
        background: "color-mix(in srgb, var(--color-neutral-900) 55%, transparent)",
      }} />
      <aside className="k-drawer" role="dialog" aria-modal="true" aria-label="Profile" style={{
        position: "fixed", zIndex: 80, top: 0, right: 0, bottom: 0, width: 400, overflow: "auto",
        background: "var(--color-bg)", boxShadow: "var(--shadow-lg)", padding: "20px 20px 40px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 52, height: 52, flex: "none", borderRadius: "50%", display: "grid", placeItems: "center",
            fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 18,
            background: "var(--color-accent-800)", color: "var(--color-accent-100)",
          }}>RK</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ margin: 0 }}>Rin Kadowaki</h4>
            <div style={{ fontSize: 12, color: mutedColor(50) }}>@rin · joined 2024</div>
          </div>
          <button className="btn btn-secondary" onClick={() => k.setProfileOpen(false)} style={{ flex: "none", padding: "4px 9px" }}>
            Close
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 16 }}>
          {stats.map((s) => (
            <div key={s.label} style={{
              padding: 11, borderRadius: 9, background: "var(--color-surface)", boxShadow: "var(--shadow-sm)",
            }}>
              <div style={{ fontFamily: "var(--font-heading)", fontSize: 22, fontWeight: 600, color: "var(--color-accent)" }}>
                {s.val}
              </div>
              <div style={{ fontSize: 10.5, color: mutedColor(50) }}>{s.label}</div>
            </div>
          ))}
        </div>

        <h6 style={{ margin: "20px 0 8px", color: mutedColor(55) }}>Taste</h6>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {taste.length
            ? taste.map((t) => <span key={t} className="tag tag-accent">{t}</span>)
            : <span style={{ fontSize: 12, color: mutedColor(50) }}>Save a few things and this fills in.</span>}
        </div>

        {/* Presets are named bundles of signal weights. They map onto
            amrec.v1.SignalWeights exactly, so the same preset can be sent to
            RecommendSimilar or to Evaluate for offline scoring. */}
        <h6 style={{ margin: "22px 0 8px", color: mutedColor(55) }}>Engine preset</h6>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {PRESETS.map((p) => (
            <Chip key={p.key} on={k.preset === p.key} onClick={() => k.setPreset(p.key)} style={{ padding: "6px 11px" }}
              title={Object.entries(p.weights).map(([sig, w]) => `${sig} ${w}`).join(" · ")}>
              {p.name}
            </Chip>
          ))}
        </div>

        <h6 style={{ margin: "22px 0 8px", color: mutedColor(55) }}>Settings</h6>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Row label="Theme">
            <Chip onClick={k.toggleTheme} style={{ padding: "6px 11px" }}>
              {k.theme === "dark" ? "Dark" : "Light"}
            </Chip>
          </Row>
          <Row label="Density">
            <Chip onClick={k.toggleDensity} style={{ padding: "6px 11px" }}>
              {k.density === "compact" ? "Compact" : "Comfortable"}
            </Chip>
          </Row>
          <Row label="Reset library" last>
            <Chip onClick={k.resetProfile} style={{ padding: "6px 11px" }}>Reset</Chip>
          </Row>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, children, last }: { label: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "9px 2px",
      borderBottom: last ? undefined : `1px solid ${mutedColor(8)}`,
    }}>
      <span style={{ flex: 1, fontSize: 13 }}>{label}</span>
      {children}
    </div>
  );
}
